import { getVisibleBounds } from "./data";
import type { ChartDataPoint } from "./data";
import { drawAreaChart, drawCandleChart, drawHollowCandleChart, drawIndicatorLine, drawLineChart, drawOHLCChart } from "./drawChartTypes";
import { drawPriceGrid, drawTimeGrid } from "./drawGrid";
import { drawPriceLabels, drawTimeLabels } from "./drawLabels";
import {
  calculateIchimoku,
  calculateParabolicSAR,
  calculatePivotPoints,
  calculateRainbowMA,
  computeIndicatorSeries,
  getIndicatorPlacement,
  INDICATOR_META,
} from "./indicators";
import { getVisibleCandleBounds, ticksToOHLC } from "./ohlc";
import type { CandleData } from "./ohlc";
import { buildPriceAxis, getAutoPricePadding, getMinimumAutoPriceRange, validateAndFixPriceRange } from "./price";
import { plotHeight, plotWidth } from "./state";
import { buildTimeAxis } from "./time";
import { priceToY, timeToX, xToTime, yToPrice } from "./transformation";
import type { State } from "./types";
import type { IndicatorConfig, IndicatorSeriesPoint } from "./indicators";
import { getChartLayout } from "./layout";
import { renderDrawings } from "./drawingTools";
import { drawVerticalDigitIndicator, isDigitsCategory, renderTrades, showSettledDigit } from "./trades";
import { getIndicatorFallbackColors, getTheme } from "./theme";

export function rgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const n = Number.parseInt(full, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const MAX_RENDERED_INDICATORS = 4;
const MAX_RENDERED_PANEL_INDICATORS = 2;

let offscreenCanvas: HTMLCanvasElement | null = null;
let offscreenDirty = true;
let offscreenSignature = "";

function getStaticSignature(state: State): string {
  const dpr = window.devicePixelRatio || 1;
  return [
    state.width,
    state.height,
    dpr.toFixed(2),
    state.left,
    state.right,
    state.top,
    state.bottom,
    state.timeStart.toFixed(2),
    state.timeEnd.toFixed(2),
    state.priceMin.toFixed(6),
    state.priceMax.toFixed(6),
    state.timeframe,
    state.themeName,
    state.displaySettings.showTimeGrid ? 1 : 0,
    state.displaySettings.showPriceGrid ? 1 : 0,
    state.displaySettings.showTimeAxis ? 1 : 0,
    state.displaySettings.showPriceAxis ? 1 : 0,
  ].join("|");
}

function getActiveIndicators(state: State): IndicatorConfig[] {
  return state.indicators.slice(0, MAX_RENDERED_INDICATORS);
}

function ensureOffscreen(state: State): HTMLCanvasElement {
  const dpr = window.devicePixelRatio || 1;
  const targetWidth = Math.max(1, Math.floor(state.width * dpr));
  const targetHeight = Math.max(1, Math.floor(state.height * dpr));

  if (!offscreenCanvas) {
    offscreenCanvas = document.createElement("canvas");
    offscreenDirty = true;
  }
  if (offscreenCanvas.width !== targetWidth || offscreenCanvas.height !== targetHeight) {
    offscreenCanvas.width = targetWidth;
    offscreenCanvas.height = targetHeight;
    offscreenDirty = true;
  }
  return offscreenCanvas;
}

function decimateTicksForRender(
  data: ChartDataPoint[],
  from: number,
  to: number,
  maxPoints: number,
): ChartDataPoint[] {
  const count = to - from;
  if (count <= 2 || count <= maxPoints) return data.slice(from, to);

  const out: ChartDataPoint[] = [];
  const bucketSize = Math.max(2, Math.ceil(count / maxPoints));

  for (let i = from; i < to; i += bucketSize) {
    const end = Math.min(to, i + bucketSize);
    const first = data[i];
    const last = data[end - 1];

    let low = first;
    let high = first;
    for (let j = i + 1; j < end; j++) {
      const p = data[j];
      if (p.quote < low.quote) low = p;
      if (p.quote > high.quote) high = p;
    }

    out.push(first);
    if (low.epoch !== first.epoch && low.epoch !== last.epoch) out.push(low);
    if (high.epoch !== first.epoch && high.epoch !== last.epoch && high.epoch !== low.epoch) out.push(high);
    if (last.epoch !== first.epoch) out.push(last);
  }

  return out;
}

function aggregateCandlesByPixelColumn(
  state: State,
  candles: CandleData[],
  from: number,
  to: number,
): CandleData[] {
  const count = to - from;
  const visibleWidth = Math.max(1, Math.floor(plotWidth(state)));
  if (count <= visibleWidth * 1.2) return candles.slice(from, to);

  const out: CandleData[] = [];
  let i = from;

  while (i < to) {
    const first = candles[i];
    const x = Math.floor(timeToX(state, first.time));

    let last = first;
    let high = first.high;
    let low = first.low;
    i++;

    while (i < to) {
      const c = candles[i];
      const cx = Math.floor(timeToX(state, c.time));
      if (cx !== x) break;
      if (c.high > high) high = c.high;
      if (c.low < low) low = c.low;
      last = c;
      i++;
    }

    out.push({
      time: first.time,
      open: first.open,
      high,
      low,
      close: last.close,
    });
  }

  return out;
}

function drawPrimaryStatic(
  state: State,
  mainPlotBottom: number,
  timeAxisTop: number,
  timeAxisBottom: number,
  timeLabelY: number,
): HTMLCanvasElement {
  const theme = getTheme(state);
  const canvas = ensureOffscreen(state);
  const sig = [
    getStaticSignature(state),
    mainPlotBottom.toFixed(2),
    timeAxisTop.toFixed(2),
    timeAxisBottom.toFixed(2),
    timeLabelY.toFixed(2),
  ].join("|");
  if (!offscreenDirty && sig === offscreenSignature) return canvas;

  const offCtx = canvas.getContext("2d");
  if (!offCtx) return canvas;
  const dpr = window.devicePixelRatio || 1;
  offCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

  offCtx.clearRect(0, 0, state.width, state.height);
  offCtx.fillStyle = theme.background;
  offCtx.fillRect(0, 0, state.width, state.height);

  const mainState: State = {
    ...state,
    bottom: state.height - mainPlotBottom,
  };

  const timeAxis = buildTimeAxis(state);
  const priceAxis = buildPriceAxis(mainState);
  
  // Only grids and background on static canvas
  if (state.displaySettings.showTimeGrid) drawTimeGrid(offCtx, mainState, timeAxis.ticks);
  if (state.displaySettings.showPriceGrid) drawPriceGrid(offCtx, mainState, priceAxis.ticks);

  // Time axis background band
  if (state.displaySettings.showTimeAxis && timeAxisBottom > timeAxisTop) {
    offCtx.save();
    offCtx.fillStyle = theme.axisBand;
    offCtx.fillRect(state.left, timeAxisTop, plotWidth(state), timeAxisBottom - timeAxisTop);
    offCtx.strokeStyle = theme.grid;
    offCtx.lineWidth = 1;
    offCtx.beginPath();
    offCtx.moveTo(state.left, timeAxisTop + 0.5);
    offCtx.lineTo(state.left + plotWidth(state), timeAxisTop + 0.5);
    offCtx.moveTo(state.left, timeAxisBottom + 0.5);
    offCtx.lineTo(state.left + plotWidth(state), timeAxisBottom + 0.5);
    offCtx.stroke();
    offCtx.restore();
  }

  offscreenDirty = false;
  offscreenSignature = sig;
  return canvas;
}

function collectSeriesMinMax(
  from: number,
  to: number,
  ...seriesList: Array<IndicatorSeriesPoint[] | undefined>
): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const series of seriesList) {
    if (!series) continue;
    for (let i = from; i < to; i++) {
      const value = series[i];
      if (value == null) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  if (min === max) {
    const pad = Math.abs(min) * 0.05 || 1;
    return { min: min - pad, max: max + pad };
  }
  const pad = (max - min) * 0.1;
  return { min: min - pad, max: max + pad };
}

function getPanelBoundsForIndicator(
  indicatorType: State["indicatorType"],
  from: number,
  to: number,
  ...seriesList: Array<IndicatorSeriesPoint[] | undefined>
): { min: number; max: number } | null {
  if (indicatorType === "rsi" || indicatorType === "stochastic") return { min: 0, max: 100 };
  if (indicatorType === "williams_r") return { min: -100, max: 0 };
  const bounds = collectSeriesMinMax(from, to, ...seriesList);
  if (!bounds) return null;
  if (indicatorType === "macd" || indicatorType === "awesome") {
    return { min: Math.min(bounds.min, 0), max: Math.max(bounds.max, 0) };
  }
  return bounds;
}

function getIndicatorInputValues(indicatorType: State["indicatorType"], candles: CandleData[]): number[] {
  if (indicatorType === "awesome") return candles.map((c) => (c.high + c.low) / 2);
  return candles.map((c) => c.close);
}

export function syncOverlayIndicatorPriceRange(state: State): void {
  if (!state.useDataRange) return;
  if (state.chartData.length === 0) return;

  // Skip price range adjustment during user interaction
  if (state.suppressMarketFollow || state.isDragging) {
    return;
  }

  const candles = ticksToOHLC(state.chartData, state.timeframe);
  const visible = getVisibleCandleBounds(candles, state.timeStart, state.timeEnd);
  if (!visible) return;

  let dataMin = Infinity;
  let dataMax = -Infinity;
  for (let i = visible.from; i < visible.to; i++) {
    const candle = candles[i];
    dataMin = Math.min(dataMin, candle.low);
    dataMax = Math.max(dataMax, candle.high);
  }
  
  if (!isFinite(dataMin) || !isFinite(dataMax)) return;

  const dataRange = dataMax - dataMin;
  const padding = getAutoPricePadding(state, dataRange);

  let finalMin = dataMin - padding;
  let finalMax = dataMax + padding;

  let range = finalMax - finalMin;
  const minimumRange = state.tightPriceScale ? getMinimumAutoPriceRange(state) : 0.1;
  if (range < minimumRange) {
    const mid = (finalMin + finalMax) / 2;
    finalMin = mid - minimumRange / 2;
    finalMax = mid + minimumRange / 2;
    range = minimumRange;
  }

  const maxRange = state.tightPriceScale
    ? Math.max(dataRange * 2, minimumRange)
    : Math.min(dataRange * 2, 20);
  if (range > maxRange) {
    const mid = (finalMin + finalMax) / 2;
    const halfRange = maxRange / 2;
    finalMin = mid - halfRange;
    finalMax = mid + halfRange;
  }
  
  state.priceMin = Math.max(0.01, finalMin);
  state.priceMax = finalMax;
  
  if (state.priceMax - state.priceMin < minimumRange) {
    const mid = (state.priceMin + state.priceMax) / 2;
    state.priceMin = mid - minimumRange / 2;
    state.priceMax = mid + minimumRange / 2;
  }
}

function drawCrosshair(
  ctx: CanvasRenderingContext2D,
  state: State,
  mainPlotBottom: number,
  timeAxisBottom: number,
): void {
  if (!state.displaySettings.showCrosshair) return;
  if (!state.crosshairInside) return;
  const theme = getTheme(state);

  const x = state.crosshairX;
  const y = state.crosshairY;
  const x0 = state.left;
  const x1 = state.left + plotWidth(state);
  const y0 = state.top;
  const y1 = mainPlotBottom;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x0, y0, x1 - x0, y1 - y0);
  ctx.clip();
  ctx.strokeStyle = theme.crosshair;
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);
  ctx.beginPath();
  ctx.moveTo(Math.round(x) + 0.5, y0);
  ctx.lineTo(Math.round(x) + 0.5, y1);
  ctx.moveTo(x0, Math.round(y) + 0.5);
  ctx.lineTo(x1, Math.round(y) + 0.5);
  ctx.stroke();
  ctx.restore();

  const price = yToPrice(state, y);
  const timestamp = xToTime(state, x);
  const d = new Date(timestamp);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  
  // ===== FORCE PRECISION FROM DATA =====
  const latestTick = state.chartData[state.chartData.length - 1];
  const pip = latestTick?.precision ?? latestTick?.pip_size ?? 2;
  const priceLabel = price.toFixed(pip);
  const timeLabel = `${hh}:${mm}:${ss}`;

  ctx.save();
  ctx.font = "11px monospace";
  ctx.textBaseline = "middle";

  const priceTextW = ctx.measureText(priceLabel).width;
  const pricePadX = 6;
  const priceBoxW = priceTextW + pricePadX * 2;
  const priceBoxH = 18;
  const priceBoxX = x1 + 4;
  const priceBoxY = y - priceBoxH / 2;
  ctx.fillStyle = theme.crosshairLabelBg;
  ctx.fillRect(priceBoxX, priceBoxY, priceBoxW, priceBoxH);
  ctx.fillStyle = theme.crosshairLabelText;
  ctx.textAlign = "left";
  ctx.fillText(priceLabel, priceBoxX + pricePadX, y);

  const timeTextW = ctx.measureText(timeLabel).width;
  const timePadX = 6;
  const timeBoxW = timeTextW + timePadX * 2;
  const timeBoxH = 18;
  const timeBoxX = x - timeBoxW / 2;
  const timeBoxY = timeAxisBottom - timeBoxH;
  ctx.fillStyle = theme.crosshairLabelBg;
  ctx.fillRect(timeBoxX, timeBoxY, timeBoxW, timeBoxH);
  ctx.fillStyle = theme.crosshairLabelText;
  ctx.textAlign = "left";
  ctx.fillText(timeLabel, timeBoxX + timePadX, timeBoxY + timeBoxH / 2);
  ctx.restore();
}

function drawLivePriceArm(ctx: CanvasRenderingContext2D, state: State, mainPlotBottom: number): void {
  if (!state.displaySettings.showLivePriceLine) return;
  if (state.chartData.length === 0) return;

  const candles = ticksToOHLC(state.chartData, state.timeframe);
  const lastCandle = candles[candles.length - 1];
  if (!lastCandle) return;

  // ===== PLOT BOUNDARIES =====
  const plotLeft = state.left;
  const plotRight = state.width - state.right;
  const plotTop = state.top;
  const plotBottom = mainPlotBottom;

  // ===== GET THE LATEST DATA POINT =====
  const latestTick = state.chartData[state.chartData.length - 1];
  if (!latestTick) return;
  
  const latestPrice = latestTick.quote;
  const latestTime = latestTick.epoch * 1000;
  
  // ===== GET PRECISION FROM THE DATA =====
  const pip = latestTick.precision ?? latestTick.pip_size ?? 3;
  
  // ===== POSITION AT THE LATEST DATA POINT =====
  const latestX = timeToX(state, latestTime);
  const latestY = priceToY(state, latestPrice);
  
  // Check if the latest price is visible on screen
  if (latestY < plotTop + 2 || latestY > plotBottom - 2) return;
  if (latestX < plotLeft || latestX > plotRight) return;

  const theme = getTheme(state);
  
  // ===== CHECK IF DATA IS RECENT (within 5 seconds) =====
  const isRecent = (Date.now() - latestTime) < 5000;
  
  // ===== USE MARKET STATUS FROM STATE, BUT ALSO CHECK IF DATA IS RECENT =====
  // If data is recent AND (market is live OR we're showing static data)
  const isLive = state.isMarketLive ?? false;
  
  // Show live indicators if:
  // 1. Market is explicitly live, OR
  // 2. Data is recent AND we're not explicitly told the market is closed
  const showLive = isLive || (isRecent && state.marketStatus !== "closed");
  
  // Determine color based on price movement
  const previousTick = state.chartData[state.chartData.length - 2];
  const isUp = previousTick ? latestPrice >= previousTick.quote : true;
  const color = showLive ? (isUp ? theme.up : theme.down) : theme.mutedText;
  
  // ===== CHECK IF USER IS VIEWING LATEST DATA =====
  const windowMs = state.timeEnd - state.timeStart;
  const expectedEnd = latestTime + (windowMs * 0.3);
  const isAtLatest = Math.abs(state.timeEnd - expectedEnd) < windowMs * 0.05;

  // ===== DRAW HORIZONTAL ARM FROM LATEST DATA POINT =====
  const plotW = plotRight - plotLeft;
  const armPx = Math.max(60, Math.min(140, plotW * 0.15));
  const armEndX = Math.min(latestX + armPx, plotRight - 8);

  ctx.save();
  ctx.beginPath();
  ctx.rect(plotLeft, plotTop, plotRight - plotLeft, plotTop + plotHeight(state) - plotTop);
  ctx.clip();

  // ===== DRAW ARM LINE FROM LATEST DATA POINT =====
  if (latestX < armEndX && latestX >= plotLeft && armEndX <= plotRight) {
    ctx.strokeStyle = showLive ? color : theme.mutedText;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 4]);
    ctx.beginPath();
    ctx.moveTo(latestX, latestY);
    ctx.lineTo(armEndX, latestY);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // ===== BLINKING DOT AT THE LATEST DATA POINT =====
  if (showLive && isAtLatest && latestX >= plotLeft && latestX <= plotRight) {
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 400);
    const outerR = 5 + pulse * 3;
    const innerR = 3.5;
    
    // Outer pulsing ring
    ctx.beginPath();
    ctx.arc(latestX, latestY, outerR, 0, Math.PI * 2);
    ctx.fillStyle = theme.up + Math.round(pulse * 0x99).toString(16).padStart(2, "0");
    ctx.fill();
    
    // Solid inner dot
    ctx.beginPath();
    ctx.arc(latestX, latestY, innerR, 0, Math.PI * 2);
    ctx.fillStyle = theme.up;
    ctx.fill();
    
    // White core
    ctx.beginPath();
    ctx.arc(latestX, latestY, 1.8, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    
    // Outer ring border
    ctx.beginPath();
    ctx.arc(latestX, latestY, outerR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 0.5;
    ctx.stroke();
  } else if (isRecent && !showLive) {
    // Data is recent but market is closed - show grey dot (no pulse)
    ctx.beginPath();
    ctx.arc(latestX, latestY, 4, 0, Math.PI * 2);
    ctx.fillStyle = theme.mutedText;
    ctx.fill();
  }

  // ===== PRICE LABEL WITH CORRECT PRECISION =====
  const label = latestPrice.toFixed(pip);
  
  ctx.font = "bold 11px monospace";
  const textW = ctx.measureText(label).width;
  const padX = 7;
  const boxH = 20;
  const boxW = textW + padX * 2;
  
  // Position badge at the end of the arm
  let badgeX = armEndX + 4;
  const rightGutter = 4;
  
  if (badgeX + boxW + rightGutter > plotRight) {
    badgeX = plotRight - boxW - rightGutter;
  }
  if (badgeX < plotLeft + 4) {
    badgeX = plotLeft + 4;
  }
  
  if (badgeX + boxW <= plotRight - 2 && badgeX >= plotLeft + 2) {
    const boxY = Math.max(plotTop + 2, Math.min(latestY - boxH / 2, plotBottom - boxH - 2));
    
    ctx.fillStyle = showLive ? color : theme.mutedText;
    if (ctx.roundRect) {
      ctx.beginPath();
      ctx.roundRect(badgeX, boxY, boxW, boxH, 4);
      ctx.fill();
    } else {
      ctx.fillRect(badgeX, boxY, boxW, boxH);
    }
    
    ctx.fillStyle = showLive ? "#ffffff" : theme.text;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(label, badgeX + padX, latestY);
  }
  
  ctx.restore();
}

function drawPanelIndicatorLine(
  ctx: CanvasRenderingContext2D,
  state: State,
  data: ChartDataPoint[],
  values: IndicatorSeriesPoint[],
  from: number,
  to: number,
  min: number,
  max: number,
  yTop: number,
  yBottom: number,
  color: string,
  lineWidth: number,
): void {
  if (to - from < 2 || values.length !== data.length || max <= min) return;
  const panelHeight = yBottom - yTop;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  let hasPath = false;
  let segmentStarted = false;
  ctx.beginPath();
  for (let i = from; i < to; i++) {
    const value = values[i];
    if (value == null) {
      segmentStarted = false;
      continue;
    }
    const x = timeToX(state, data[i].epoch * 1000);
    const y = yTop + (1 - (value - min) / (max - min)) * panelHeight;
    if (!segmentStarted) {
      ctx.moveTo(x, y);
      segmentStarted = true;
      hasPath = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  if (hasPath) ctx.stroke();
  ctx.restore();
}

function drawPanelHistogram(
  ctx: CanvasRenderingContext2D,
  state: State,
  data: ChartDataPoint[],
  values: IndicatorSeriesPoint[],
  from: number,
  to: number,
  min: number,
  max: number,
  yTop: number,
  yBottom: number,
): void {
  if (to - from < 1 || values.length !== data.length || max <= min) return;
  const panelHeight = yBottom - yTop;
  const zeroY = yTop + (1 - (0 - min) / (max - min)) * panelHeight;
  const stepPx = to - from > 1 ? Math.abs(timeToX(state, data[from + 1].epoch * 1000) - timeToX(state, data[from].epoch * 1000)) : 4;
  const barWidth = Math.max(1, Math.min(10, stepPx * 0.7));

  ctx.save();
  for (let i = from; i < to; i++) {
    const value = values[i];
    if (value == null) continue;
    const x = timeToX(state, data[i].epoch * 1000);
    const y = yTop + (1 - (value - min) / (max - min)) * panelHeight;
    ctx.fillStyle = value >= 0 ? "rgba(38, 166, 154, 0.65)" : "rgba(239, 83, 80, 0.65)";
    ctx.fillRect(x - barWidth / 2, Math.min(y, zeroY), barWidth, Math.max(1, Math.abs(zeroY - y)));
  }
  ctx.restore();
}

function drawOverlayRainbowMA(
  ctx: CanvasRenderingContext2D,
  state: State,
  candles: CandleData[],
  visibleFrom: number,
  visibleTo: number,
  lineWidth: number,
  color: string,
  params: Record<string, any> = {},
): void {
  const prices = candles.map((c) => c.close);
  const periods = Array.isArray(params.periods) && params.periods.length > 0
    ? params.periods.map((value: unknown) => Math.max(2, Math.floor(Number(value) || 2)))
    : [8, 13, 21, 34, 55, 89, 144, 233];
  const palette = Array.isArray(params.colors) && params.colors.length > 0
    ? params.colors
    : ["#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e", "#14b8a6", "#3b82f6"];
  const lines = calculateRainbowMA(prices, periods);
  const candleSeries: ChartDataPoint[] = candles.map((c) => ({
    epoch: Math.floor(c.time / 1000),
    quote: c.close,
    symbol: state.chartData[0]?.symbol ?? "",
    precision: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
    pip_size: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
  }));

  for (let i = 0; i < lines.length; i++) {
    drawIndicatorLine(
      ctx,
      state,
      candleSeries,
      lines[i],
      visibleFrom,
      visibleTo,
      palette[i % palette.length] ?? color,
      Math.max(1, lineWidth - 1),
    );
  }
}

function drawOverlayIchimoku(
  ctx: CanvasRenderingContext2D,
  state: State,
  candles: CandleData[],
  visibleFrom: number,
  visibleTo: number,
  color: string,
  lineWidth: number,
  params: Record<string, any> = {},
): void {
  const tenkanPeriod = Math.max(2, Math.floor(Number(params.tenkanPeriod) || 9));
  const kijunPeriod = Math.max(2, Math.floor(Number(params.kijunPeriod) || 26));
  const senkouPeriod = Math.max(2, Math.floor(Number(params.senkouPeriod) || 52));
  const tenkanColor = params.tenkanColor || color;
  const kijunColor = params.kijunColor || "#3b82f6";
  const cloudUpColor = params.cloudUpColor || "rgba(34, 197, 94, 0.18)";
  const cloudDownColor = params.cloudDownColor || "rgba(239, 68, 68, 0.18)";
  const prices = candles.map((c) => c.close);
  const series = calculateIchimoku(prices, tenkanPeriod, kijunPeriod, senkouPeriod);
  const candleSeries: ChartDataPoint[] = candles.map((c) => ({
    epoch: Math.floor(c.time / 1000),
    quote: c.close,
    symbol: state.chartData[0]?.symbol ?? "",
    precision: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
    pip_size: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
  }));

  ctx.save();
  ctx.beginPath();
  ctx.rect(state.left, state.top, plotWidth(state), plotHeight(state));
  ctx.clip();

  for (let i = Math.max(visibleFrom + 1, 1); i < visibleTo; i++) {
    const a0 = series.senkouA[i - 1];
    const a1 = series.senkouA[i];
    const b0 = series.senkouB[i - 1];
    const b1 = series.senkouB[i];
    if (a0 == null || a1 == null || b0 == null || b1 == null) continue;

    const x0 = timeToX(state, candles[i - 1].time);
    const x1 = timeToX(state, candles[i].time);
    const yA0 = priceToY(state, a0);
    const yA1 = priceToY(state, a1);
    const yB0 = priceToY(state, b0);
    const yB1 = priceToY(state, b1);

    ctx.fillStyle = a1 >= b1 ? cloudUpColor : cloudDownColor;
    ctx.beginPath();
    ctx.moveTo(x0, yA0);
    ctx.lineTo(x1, yA1);
    ctx.lineTo(x1, yB1);
    ctx.lineTo(x0, yB0);
    ctx.closePath();
    ctx.fill();
  }

  drawIndicatorLine(ctx, state, candleSeries, series.tenkan, visibleFrom, visibleTo, tenkanColor, lineWidth);
  drawIndicatorLine(ctx, state, candleSeries, series.kijun, visibleFrom, visibleTo, kijunColor, lineWidth);
  ctx.restore();
}

function drawOverlayParabolicSAR(
  ctx: CanvasRenderingContext2D,
  state: State,
  candles: CandleData[],
  visibleFrom: number,
  visibleTo: number,
  color: string,
  params: Record<string, any> = {},
): void {
  const prices = candles.map((c) => c.close);
  const acceleration = Math.max(0.001, Number(params.step) || 0.02);
  const maximum = Math.max(acceleration, Number(params.max) || 0.2);
  const values = calculateParabolicSAR(prices, acceleration, maximum);
  const dotColor = color || "#ef4444";

  ctx.save();
  ctx.fillStyle = dotColor;
  for (let i = visibleFrom; i < visibleTo; i++) {
    const value = values[i];
    if (value == null) continue;
    const x = timeToX(state, candles[i].time);
    const y = priceToY(state, value);
    if (y < state.top - 10 || y > plotHeight(state) + state.top + 10) continue;
    ctx.beginPath();
    ctx.arc(x, y, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function drawOverlayPivotPoints(
  ctx: CanvasRenderingContext2D,
  state: State,
  candles: CandleData[],
  visibleFrom: number,
  visibleTo: number,
  color: string,
  lineWidth: number,
  params: Record<string, any> = {},
): void {
  const prices = candles.map((c) => c.close);
  const period = Math.max(2, Math.floor(Number(params.period) || 20));
  const series = calculatePivotPoints(prices, period);
  const latest = series.levels.slice(visibleFrom, visibleTo).reverse().find((value) => value !== null);
  if (!latest) return;

  const levels = [
    { key: "r3", value: latest.r3 },
    { key: "r2", value: latest.r2 },
    { key: "r1", value: latest.r1 },
    { key: "pp", value: latest.pp },
    { key: "s1", value: latest.s1 },
    { key: "s2", value: latest.s2 },
    { key: "s3", value: latest.s3 },
  ];

  ctx.save();
  ctx.strokeStyle = color || "#84cc16";
  ctx.lineWidth = Math.max(1, lineWidth - 0.5);
  ctx.setLineDash([5, 4]);
  for (const level of levels) {
    const y = priceToY(state, level.value);
    if (y < state.top - 5 || y > state.top + plotHeight(state) + 5) continue;
    ctx.beginPath();
    ctx.moveTo(state.left, y);
    ctx.lineTo(state.left + plotWidth(state), y);
    ctx.stroke();
  }
  ctx.setLineDash([]);
  ctx.restore();
}

function drawPanelGuides(
  ctx: CanvasRenderingContext2D,
  state: State,
  yTop: number,
  yBottom: number,
  min: number,
  max: number,
  levels: number[],
): void {
  if (max <= min) return;
  const h = yBottom - yTop;
  ctx.save();
  ctx.strokeStyle = "#cbd5e1";
  ctx.fillStyle = "#64748b";
  ctx.lineWidth = 1;
  ctx.font = "11px monospace";
  for (const level of levels) {
    if (level < min || level > max) continue;
    const y = yTop + (1 - (level - min) / (max - min)) * h;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(state.left, y);
    ctx.lineTo(state.left + plotWidth(state), y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillText(level.toFixed(0), state.left + plotWidth(state) - 22, y - 4);
  }
  ctx.restore();
}

export function drawChart(ctx: CanvasRenderingContext2D, state: State): void {
  validateAndFixPriceRange(state);
  const theme = getTheme(state);
  const fallbackColors = getIndicatorFallbackColors(state);
  if (state.priceMax <= state.priceMin) state.priceMax = state.priceMin + 1;
  const activeIndicators = getActiveIndicators(state);
  const overlayIndicators = activeIndicators.filter((indicator) => getIndicatorPlacement(indicator.type) === "overlay");
  const panelIndicators = activeIndicators
    .filter((indicator) => getIndicatorPlacement(indicator.type) === "panel")
    .slice(0, MAX_RENDERED_PANEL_INDICATORS);
  const {
    mainPlotBottom,
    timeAxisTop,
    timeAxisBottom,
    panelTop,
    panelBottom,
    timeLabelY,
    mainState,
  } = getChartLayout(state);

  // 1. Draw static canvas (background, grids only)
  const staticCanvas = drawPrimaryStatic(state, mainPlotBottom, timeAxisTop, timeAxisBottom, timeLabelY);
  ctx.drawImage(staticCanvas, 0, 0, state.width, state.height);

  // 2. Draw chart content (candles, lines, indicators)
  const plotLeft = state.left;
  const plotRight = state.width - state.right;
  const plotTop = state.top;
  const plotBottom = mainPlotBottom;

  ctx.save();
  ctx.beginPath();
  ctx.rect(plotLeft, plotTop, plotRight - plotLeft, plotBottom - plotTop);
  ctx.clip();

  if (state.chartData.length > 0) {
    const maxDrawablePoints = Math.max(200, Math.floor(plotWidth(state) * 1.5));
    const visibleBounds = getVisibleBounds(state.chartData, state.timeStart, state.timeEnd);

    if (visibleBounds) {
      const candles = ticksToOHLC(state.chartData, state.timeframe);

      if (state.chartType === "line") {
        const reduced = decimateTicksForRender(state.chartData, visibleBounds.from, visibleBounds.to, maxDrawablePoints);
        drawLineChart(ctx, mainState, reduced, 0, reduced.length, theme.up, 2);
      } else if (state.chartType === "area") {
        const reduced = decimateTicksForRender(state.chartData, visibleBounds.from, visibleBounds.to, maxDrawablePoints);
        drawAreaChart(ctx, mainState, reduced, 0, reduced.length, theme.up);
      } else {
        const visibleCandles = getVisibleCandleBounds(candles, state.timeStart, state.timeEnd);
        if (visibleCandles) {
          const merged = aggregateCandlesByPixelColumn(state, candles, visibleCandles.from, visibleCandles.to);
          if (state.chartType === "candle") {
            drawCandleChart(ctx, mainState, merged, 0, merged.length, theme.up, theme.down, theme.wick);
          } else if (state.chartType === "hollow") {
            drawHollowCandleChart(ctx, mainState, merged, 0, merged.length, theme.up, theme.down, theme.wick);
          } else if (state.chartType === "ohlc") {
            drawOHLCChart(ctx, mainState, merged, 0, merged.length, theme.up, theme.down);
          }
        }
      }

      if (overlayIndicators.length > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(state.left, state.top, plotWidth(state), mainPlotBottom - state.top);
        ctx.clip();

        const indicatorSeries: ChartDataPoint[] = candles.map((c) => ({
          epoch: Math.floor(c.time / 1000),
          quote: c.close,
          symbol: state.chartData[0]?.symbol ?? "",
          precision: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
          pip_size: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
        }));
        const candleVisibleBounds = getVisibleBounds(indicatorSeries, state.timeStart, state.timeEnd);
        if (candleVisibleBounds) {
          for (const indicator of overlayIndicators) {
            const period = Math.max(2, Math.floor(indicator.period));
            const defaultColor = indicator.type === "sma" ? fallbackColors.primary : fallbackColors.secondary;
            const lineColor = indicator.color || defaultColor;
            const lineWidth = Math.max(1, Math.min(6, indicator.lineWidth || 2));
            if (indicator.type === "rainbow_ma") {
              drawOverlayRainbowMA(
                ctx,
                mainState,
                candles,
                candleVisibleBounds.from,
                candleVisibleBounds.to,
                lineWidth,
                lineColor,
                (indicator as any).params ?? {},
              );
              continue;
            }
            if (indicator.type === "ichimoku") {
              drawOverlayIchimoku(
                ctx,
                mainState,
                candles,
                candleVisibleBounds.from,
                candleVisibleBounds.to,
                lineColor,
                lineWidth,
                (indicator as any).params ?? {},
              );
              continue;
            }
            if (indicator.type === "parabolic_sar") {
              drawOverlayParabolicSAR(
                ctx,
                mainState,
                candles,
                candleVisibleBounds.from,
                candleVisibleBounds.to,
                lineColor,
                (indicator as any).params ?? {},
              );
              continue;
            }
            if (indicator.type === "pivot_points") {
              drawOverlayPivotPoints(
                ctx,
                mainState,
                candles,
                candleVisibleBounds.from,
                candleVisibleBounds.to,
                lineColor,
                lineWidth,
                (indicator as any).params ?? {},
              );
              continue;
            }

            const prices = getIndicatorInputValues(indicator.type, candles);
            const series = computeIndicatorSeries(prices, indicator.type, period);
            drawIndicatorLine(ctx, mainState, indicatorSeries, series.primary, candleVisibleBounds.from, candleVisibleBounds.to, lineColor, lineWidth);
            if (series.secondary) {
              drawIndicatorLine(ctx, mainState, indicatorSeries, series.secondary, candleVisibleBounds.from, candleVisibleBounds.to, `${lineColor}B0`, Math.max(1, lineWidth - 1));
            }
            if (series.tertiary) {
              drawIndicatorLine(ctx, mainState, indicatorSeries, series.tertiary, candleVisibleBounds.from, candleVisibleBounds.to, `${lineColor}B0`, Math.max(1, lineWidth - 1));
            }
          }
        }

        ctx.restore();
      }
    }

    // Panel indicators need their own clip region below the main price plot.
    // The outer plot clip is intentionally released here so the lower panes can render.
    ctx.restore();

    if (panelIndicators.length > 0) {
      const candles = ticksToOHLC(state.chartData, state.timeframe);
      const candleSeries: ChartDataPoint[] = candles.map((c) => ({
        epoch: Math.floor(c.time / 1000),
        quote: c.close,
        symbol: state.chartData[0]?.symbol ?? "",
        precision: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
        pip_size: state.chartData[0]?.precision ?? state.chartData[0]?.pip_size ?? 0,
      }));
      const visibleIndicatorBounds = getVisibleBounds(candleSeries, state.timeStart, state.timeEnd);
      const visibleFrom = visibleIndicatorBounds?.from ?? 0;
      const visibleTo = visibleIndicatorBounds?.to ?? candleSeries.length;
      const panelAreaHeight = panelBottom - panelTop;
      const perPanelHeight = panelAreaHeight / panelIndicators.length;

      ctx.save();
      ctx.beginPath();
      ctx.rect(state.left, panelTop, plotWidth(state), panelBottom - panelTop);
      ctx.clip();

      for (let panelIndex = 0; panelIndex < panelIndicators.length; panelIndex++) {
        const indicator = panelIndicators[panelIndex];
        const paneTop = panelTop + perPanelHeight * panelIndex;
        const paneBottom = panelIndex === panelIndicators.length - 1 ? panelBottom : paneTop + perPanelHeight - 2;
        const prices = candles.map((c) => c.close);
        const period = Math.max(2, Math.floor(indicator.period));
        const series = computeIndicatorSeries(prices, indicator.type, period);
        const defaultColor = indicator.type === "sma" ? fallbackColors.primary : fallbackColors.secondary;
        const lineColor = indicator.color || defaultColor;
        const lineWidth = Math.max(1, Math.min(6, indicator.lineWidth || 2));
        if (paneBottom - paneTop < 20) continue;
        const computedBounds = getPanelBoundsForIndicator(
          indicator.type,
          visibleFrom,
          visibleTo,
          series.primary,
          series.secondary,
          series.tertiary,
        );
        const bounds =
          computedBounds ??
          (indicator.type === "rsi" || indicator.type === "stochastic"
            ? { min: 0, max: 100 }
            : indicator.type === "williams_r"
              ? { min: -100, max: 0 }
              : { min: -1, max: 1 });

        ctx.save();
        ctx.fillStyle = theme.panelBackground;
        ctx.fillRect(state.left, paneTop, plotWidth(state), paneBottom - paneTop);
        ctx.strokeStyle = theme.panelBorder;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(state.left, paneTop);
        ctx.lineTo(state.left + plotWidth(state), paneTop);
        ctx.stroke();
        ctx.fillStyle = theme.text;
        ctx.font = "600 11px monospace";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(INDICATOR_META[indicator.type].label, state.left + 8, paneTop + 6);

        if (indicator.type === "rsi") drawPanelGuides(ctx, state, paneTop, paneBottom, bounds.min, bounds.max, [30, 50, 70]);
        if (indicator.type === "stochastic") drawPanelGuides(ctx, state, paneTop, paneBottom, bounds.min, bounds.max, [20, 50, 80]);
        if (indicator.type === "williams_r") drawPanelGuides(ctx, state, paneTop, paneBottom, bounds.min, bounds.max, [-80, -50, -20]);
        if (indicator.type === "macd" || indicator.type === "awesome") drawPanelGuides(ctx, state, paneTop, paneBottom, bounds.min, bounds.max, [0]);

        ctx.beginPath();
        ctx.rect(state.left, paneTop, plotWidth(state), paneBottom - paneTop);
        ctx.clip();
        if (indicator.type === "macd") {
          if (series.tertiary) drawPanelHistogram(ctx, state, candleSeries, series.tertiary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom);
          drawPanelIndicatorLine(ctx, state, candleSeries, series.primary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom, lineColor, lineWidth);
          if (series.secondary) {
            drawPanelIndicatorLine(ctx, state, candleSeries, series.secondary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom, theme.down, Math.max(1, lineWidth - 1));
          }
        } else if (indicator.type === "awesome") {
          drawPanelHistogram(ctx, state, candleSeries, series.primary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom);
        } else {
          drawPanelIndicatorLine(ctx, state, candleSeries, series.primary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom, lineColor, lineWidth);
          if (series.secondary) {
            drawPanelIndicatorLine(ctx, state, candleSeries, series.secondary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom, fallbackColors.secondary, Math.max(1, lineWidth - 1));
          }
          if (series.tertiary) {
            drawPanelIndicatorLine(ctx, state, candleSeries, series.tertiary, visibleFrom, visibleTo, bounds.min, bounds.max, paneTop, paneBottom, fallbackColors.tertiary, Math.max(1, lineWidth - 1));
          }
        }
        ctx.restore();
      }

      ctx.restore();
    }
  }

  // 3. Draw overlays
  renderDrawings(ctx, state);
  drawLivePriceArm(ctx, panelIndicators.length > 0 ? mainState : state, mainPlotBottom);
  renderTrades(ctx, state);
  drawCrosshair(ctx, state, mainPlotBottom, timeAxisBottom);

  // 4. Draw digit indicators
  const settledDigitTrade = state.trades.find(t => 
    isDigitsCategory(t.contractType) && 
    (t.status === "won" || t.status === "lost") &&
    t.exitPrice !== undefined
  );

  if (settledDigitTrade && settledDigitTrade.exitPrice !== undefined) {
    showSettledDigit(
      settledDigitTrade.exitPrice,
      settledDigitTrade.contractType,
      settledDigitTrade.status as "won" | "lost",
      settledDigitTrade.digitTarget,
      settledDigitTrade.digitLow,
      settledDigitTrade.digitHigh
    );
  }

  const hasDigitsContract = (state.selectedContractType && isDigitsCategory(state.selectedContractType)) ||
    state.trades.some(trade => isDigitsCategory(trade.contractType));

  if (hasDigitsContract && state.chartData.length > 0) {
    const currentTick = state.chartData[state.chartData.length - 1];
    const currentPrice = currentTick?.quote;
    if (currentPrice !== undefined && currentPrice !== null) {
      const digitTrades = state.trades.filter(t => isDigitsCategory(t.contractType));
      const activeTrade = [...digitTrades].reverse().find(t => t.status === "active") ?? digitTrades.at(-1);
      const activeContract = state.selectedContractType || activeTrade?.contractType;
      const isSettled = activeTrade?.status === "won" || activeTrade?.status === "lost";

      drawVerticalDigitIndicator(
        ctx,                                    // 1: CanvasRenderingContext2D
        state,                                  // 2: State
        currentPrice,                           // 3: currentPrice
        activeContract ? String(activeContract) : undefined, // 4: contractType
        activeTrade?.digitTarget,               // 5: digitTarget
        activeTrade?.digitLow,                  // 6: digitLow
        activeTrade?.digitHigh,                 // 7: digitHigh
        isSettled                               // 8: isSettled
      );
    }
  }
  
  // Draw time labels
  if (state.displaySettings.showTimeAxis) {
    const timeAxis = buildTimeAxis(state);
    drawTimeLabels(ctx, state, timeAxis.labels);
  }
  
  // Draw price labels
  if (state.displaySettings.showPriceAxis) {
    const priceAxis = buildPriceAxis(mainState);
    drawPriceLabels(ctx, mainState, priceAxis.labels);
  }
}
